import type {
  AcceptTaskStatus,
  AgentDelegationReason,
  AgentRunCreatableRuntime,
  AgentRunDisplayStatus,
  AgentRunEventSource,
  AgentRunEventType,
  AgentRunFailureCode,
  AgentRunFileChange,
  AgentRunListScope,
  AgentRunStatus,
  AgentRuntime,
  ArtifactKind,
  CliAuthStatus,
  RunCancelReason,
  RunnerStatus,
  RunPhase,
} from '../contracts/agents';
import type { StatusTone } from '../contracts/common';

/** Statusy przebiegu (Fala 5). */
export const AGENT_RUN_STATUS_LABELS: Record<AgentRunStatus, string> = {
  queued: 'W kolejce',
  running: 'Pracuje',
  waiting_for_approval: 'Czeka na akceptację',
  succeeded: 'Gotowe',
  failed: 'Błąd',
  cancelled: 'Anulowane',
};

export const AGENT_RUN_STATUS_TONES: Record<AgentRunStatus, StatusTone> = {
  queued: 'neutral',
  running: 'accent',
  waiting_for_approval: 'accent',
  succeeded: 'success',
  failed: 'danger',
  cancelled: 'neutral',
};

/** Status do wyświetlenia (Figma 11): sukces bez odbioru = „Do odbioru”. */
export const AGENT_RUN_DISPLAY_STATUS_LABELS: Record<AgentRunDisplayStatus, string> = {
  ...AGENT_RUN_STATUS_LABELS,
  to_review: 'Do odbioru',
};

export const AGENT_RUN_DISPLAY_STATUS_TONES: Record<AgentRunDisplayStatus, StatusTone> = {
  ...AGENT_RUN_STATUS_TONES,
  to_review: 'accent',
};

/** Dopisek przy koszcie runu z Claude Code CLI przy subskrypcji (Fala 6: koszt informacyjny, poza budżetem AI). */
export const AGENT_RUN_COST_INFORMATIONAL_LABEL = 'koszt wg CLI (subskrypcja, informacyjnie)';

export const AGENT_RUNTIME_LABELS: Record<AgentRuntime, string> = {
  local_runner: 'Lokalny runner',
  external: 'Zewnętrzny agent (MCP)',
  /** wyłączony w Fali 9 (DECISIONS 2026-10-07) — etykieta tylko dla historii */
  api: 'API (wyłączone)',
};

/**
 * Wykonawca w dialogu „Zleć Claude” i w podglądzie zlecenia Copilota (Fala 9): zmiany w kodzie i operacje robi Claude —
 * Claude Code na komputerze osoby (lokalny runner) albo sesja Claude podłączona przez MCP (`external`).
 */
export const DELEGATE_RUNTIME_LABELS: Record<AgentRunCreatableRuntime, string> = {
  local_runner: 'Claude na moim komputerze (runner)',
  external: 'Sesja Claude (MCP)',
};

/**
 * Dlaczego taki wykonawca w podglądzie zlecenia (`agents.delegationPreview`) — samo „dlaczego”; kto wykona, mówi
 * etykieta wykonawcy (`DELEGATE_RUNTIME_LABELS`) obok.
 */
export function delegationReasonText(d: { reason: AgentDelegationReason; runner: { name: string } | null }): string {
  switch (d.reason) {
    case 'runner_online':
      return `Komputer „${d.runner?.name ?? 'runner'}” jest aktywny i ma repozytorium projektu.`;
    case 'runner_offline':
      return 'Żaden Twój komputer z repozytorium projektu nie jest teraz aktywny.';
    case 'no_repo':
      return 'Projekt nie ma repozytorium.';
    default:
      return 'Zlecenie bez projektu z repozytorium.';
  }
}

/** Powód błędu w liście (Figma 11: „Przerwany: limit czasu”). */
export const AGENT_RUN_FAILURE_LABELS: Record<AgentRunFailureCode, string> = {
  timeout: 'Przerwany: limit czasu',
  cost_limit: 'Przerwany: limit kosztu',
  turns_limit: 'Przerwany: limit kroków',
  runner_lost: 'Utracono kontakt z runnerem',
  runner_restarted: 'Runner uruchomiony ponownie w trakcie pracy',
  runner_unavailable: 'Nikt nie odebrał zlecenia',
  agent_idle: 'Agent przestał odpowiadać',
  cli_unavailable: 'Brak Claude Code na komputerze runnera',
  cli_auth: 'Claude Code wymaga ponownego logowania',
  repo_not_allowed: 'Repozytorium spoza listy runnera',
  repo_error: 'Błąd repozytorium',
  tests_failed: 'Testy nie przechodzą',
  agent_error: 'Agent zakończył się błędem',
  approval_rejected: 'Akceptacja odrzucona',
  approval_expired: 'Akceptacja wygasła',
  internal: 'Błąd systemu',
};

/** Kolumna „Wykonawca” (Figma 11: „Worker · Claude Code”). */
export function agentExecutorLabel(run: { runtime: AgentRuntime | string; provider: string | null }): string {
  const provider = run.provider ? (AGENT_PROVIDER_LABELS[run.provider] ?? run.provider) : null;
  switch (run.runtime) {
    case 'local_runner':
      return `Worker · ${provider ?? 'Claude Code'}`;
    case 'external':
      return provider ? `Agent · ${provider}` : 'Zewnętrzny agent';
    default:
      return provider ? `API · ${provider}` : 'API';
  }
}

export const AGENT_PROVIDER_LABELS: Record<string, string> = {
  claude_code: 'Claude Code',
  claude: 'Claude',
  codex: 'Codex',
  astra: 'Astra',
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  fake: 'Symulacja',
};

export const AGENT_RUN_EVENT_TYPE_LABELS: Record<AgentRunEventType, string> = {
  status: 'Status',
  log: 'Dziennik',
  command: 'Polecenie',
  tool: 'Narzędzie',
  git: 'Git',
  file_change: 'Zmiany w plikach',
  test: 'Testy',
  artifact: 'Artefakt',
  approval: 'Akceptacja',
  warning: 'Ostrzeżenie',
  error: 'Błąd',
  summary: 'Podsumowanie',
};

export const AGENT_RUN_EVENT_SOURCE_LABELS: Record<AgentRunEventSource, string> = {
  runner: 'Runner',
  agent: 'Agent',
  server: 'System',
  user: 'Osoba',
};

export const AGENT_RUN_FILE_CHANGE_LABELS: Record<AgentRunFileChange, string> = {
  added: 'Dodany',
  modified: 'Zmieniony',
  deleted: 'Usunięty',
  renamed: 'Przeniesiony',
};

export const ARTIFACT_KIND_LABELS: Record<ArtifactKind, string> = {
  file: 'Plik',
  link: 'Link',
  pr: 'Pull request',
  diff: 'Zmiany (diff)',
  report: 'Raport',
  test_log: 'Log testów',
};

export const RUNNER_STATUS_LABELS: Record<RunnerStatus, string> = {
  online: 'Online',
  offline: 'Offline',
  disabled: 'Wyłączony',
};

export const RUNNER_STATUS_TONES: Record<RunnerStatus, StatusTone> = {
  online: 'success',
  offline: 'neutral',
  disabled: 'neutral',
};

export const CLI_AUTH_STATUS_LABELS: Record<CliAuthStatus, string> = {
  ok: 'Zalogowane',
  expired: 'Logowanie wygasło — uruchom claude i /login',
  unknown: 'Nie sprawdzono',
};

export const RUN_PHASE_LABELS: Record<RunPhase, string> = {
  preparing: 'Przygotowanie',
  agent: 'Agent pracuje',
  tests: 'Testy',
  diff: 'Zbieranie zmian',
  commit: 'Commit',
  push: 'Wysyłka brancha',
  pr: 'Pull request',
  uploading: 'Wysyłka wyniku',
  cleanup: 'Sprzątanie',
};

export const RUN_CANCEL_REASON_LABELS: Record<RunCancelReason, string> = {
  user: 'Anulowane przez osobę',
  timeout: 'Przekroczony limit czasu',
  cost_limit: 'Przekroczony limit kosztu',
  turns_limit: 'Przekroczony limit kroków',
  runner_disabled: 'Runner wyłączony',
};

/** Nota pod listą przebiegów (Figma 11). */
export const AGENTS_SAFETY_NOTE = 'AI nie wdraża na produkcję, nie wysyła maili i nie wystawia faktur bez akceptacji.';
/** Nota pod blokiem „AI skończyło pracę” (Figma 04). */
export const AGENT_DEPLOY_SEPARATE_NOTE = 'Wdrożenie na produkcję to osobna decyzja, zapytam o nią po odbiorze.';

/** Status zadania po odbiorze wyniku. */
export const ACCEPT_TASK_STATUS_LABELS: Record<AcceptTaskStatus, string> = {
  review: 'Do odbioru',
  done: 'Gotowe',
};

/** Przełącznik listy przebiegów. */
export const AGENT_RUN_SCOPE_LABELS: Record<AgentRunListScope, string> = {
  mine: 'Moje',
  team: 'Zespół',
};

/**
 * Tytuł bloku wyniku na karcie zadania (Figma 04) wg stanu runu. Zlecenie dla sesji Claude (`external` w kolejce,
 * Fala 9) czeka na sesję, nie na runner.
 */
export function agentRunBlockTitle(status: AgentRunDisplayStatus, runtime?: AgentRuntime | string): string {
  switch (status) {
    case 'queued':
      return runtime === 'external' ? 'Claude czeka na sesję (MCP)' : 'AI czeka na runner';
    case 'running':
      return 'AI pracuje';
    case 'waiting_for_approval':
      return 'AI czeka na akceptację';
    case 'to_review':
      return 'AI skończyło pracę';
    case 'succeeded':
      return 'Wynik odebrany';
    case 'cancelled':
      return 'Przebieg anulowany';
    default:
      return 'Przebieg AI nie powiódł się';
  }
}

/**
 * Instrukcja uruchomienia runnera („Dodaj komputer”, RUNNER_PROTOCOL §6): token w zmiennej albo w pliku tylko dla
 * użytkownika, repozytoria w lokalnej konfiguracji, Claude Code zalogowane na tym komputerze (serwer nigdy nie zna
 * danych logowania do Claude).
 */
export function runnerSetupSteps(serverUrl: string): { title: string; detail: string }[] {
  const server = serverUrl.replace(/\/+$/, '');
  return [
    {
      title: 'Zapisz token na tym komputerze',
      detail:
        'W zmiennej FOS_RUNNER_TOKEN albo w pliku %APPDATA%\\fravity-runner\\token (tylko dla Twojego konta). Nie wklejaj go do repozytorium.',
    },
    {
      title: 'Wskaż repozytoria',
      detail: `W %APPDATA%\\fravity-runner\\config.json: "server": "${server}", lista "repos" (adres, ścieżka lokalna, komenda testów). Runner odbiera tylko zlecenia dla tych repozytoriów.`,
    },
    {
      title: 'Zaloguj Claude Code',
      detail: 'Uruchom claude i /login na tym komputerze. Fravity OS nie przechowuje danych logowania do Claude.',
    },
    {
      title: 'Uruchom runner',
      detail: 'Runner (apps/runner) zgłosi się w ciągu 30 s — status zmieni się na Online.',
    },
  ];
}
